import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ExternalLink, Play, Pause, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Monitor, MonitorRun, ScheduleConfig, cronToSchedule, scheduleToCron } from '../../../../types/monitoring';
import { apiClient } from '../../../../lib/api';
import { useToast } from '../../../../hooks/useToast';
import { ConfirmationModal } from '../../../ui/ConfirmationModal';
import { Button } from '../../../ui/Button';
import { MonitorInfoCard } from './MonitorInfoCard';
import { MonitorScheduleEditor } from './MonitorScheduleEditor';
import { MonitorRunHistory } from './MonitorRunHistory';

interface MonitorDetailViewProps {
  monitor: Monitor;
  onBack: () => void;
  onMonitorUpdated: () => void;
}

export const MonitorDetailView: React.FC<MonitorDetailViewProps> = ({
  monitor,
  onBack,
  onMonitorUpdated,
}) => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [currentMonitor, setCurrentMonitor] = useState<Monitor>(monitor);
  const [monitorRuns, setMonitorRuns] = useState<MonitorRun[]>([]);
  const [isLoadingRuns, setIsLoadingRuns] = useState(false);
  const [isUpdatingMonitor, setIsUpdatingMonitor] = useState(false);
  const [isEditingSchedule, setIsEditingSchedule] = useState(false);
  const [editSchedule, setEditSchedule] = useState<ScheduleConfig>(
    cronToSchedule(monitor.cron_expression, monitor.timezone)
  );
  const [editMaxResults, setEditMaxResults] = useState(monitor.max_results);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeletingMonitor, setIsDeletingMonitor] = useState(false);

  const isEnabled = currentMonitor.status === 'enabled';

  const loadMonitorRuns = useCallback(async () => {
    setIsLoadingRuns(true);
    try {
      const response = await apiClient.listMonitorRuns(currentMonitor.id, 10);
      setMonitorRuns(response.runs || []);
    } catch (error) {
      console.error('Error loading monitor runs:', error);
    } finally {
      setIsLoadingRuns(false);
    }
  }, [currentMonitor.id]);

  useEffect(() => {
    loadMonitorRuns();
  }, [loadMonitorRuns]);

  const handleToggleStatus = async () => {
    setIsUpdatingMonitor(true);
    try {
      const newStatus = isEnabled ? 'disabled' : 'enabled';
      const updatedMonitor = await apiClient.updateMonitor(currentMonitor.id, { status: newStatus });
      setCurrentMonitor(updatedMonitor);
      onMonitorUpdated();
    } catch (error) {
      console.error('Error toggling monitor status:', error);
      showToast('Failed to update monitor status', 'error');
    } finally {
      setIsUpdatingMonitor(false);
    }
  };

  const handleSaveSchedule = async () => {
    setIsUpdatingMonitor(true);
    try {
      const updatedMonitor = await apiClient.updateMonitor(currentMonitor.id, {
        cron_expression: scheduleToCron(editSchedule),
        timezone: editSchedule.timezone,
        max_results: editMaxResults,
      });
      setCurrentMonitor(updatedMonitor);
      setIsEditingSchedule(false);
      onMonitorUpdated();
    } catch (error) {
      console.error('Error saving schedule:', error);
      showToast('Failed to save schedule', 'error');
    } finally {
      setIsUpdatingMonitor(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditingSchedule(false);
    setEditSchedule(cronToSchedule(currentMonitor.cron_expression, currentMonitor.timezone));
    setEditMaxResults(currentMonitor.max_results);
  };

  const handleDeleteMonitor = async () => {
    setIsDeletingMonitor(true);
    try {
      await apiClient.deleteMonitor(currentMonitor.id);
      setShowDeleteConfirm(false);
      onMonitorUpdated();
      onBack();
    } catch (error) {
      console.error('Error deleting monitor:', error);
      showToast('Failed to delete monitor', 'error');
    } finally {
      setIsDeletingMonitor(false);
    }
  };

  const handleGoToDataset = () => {
    if (currentMonitor.lead_table_id) {
      navigate(`/datasets/${currentMonitor.lead_table_id}`);
    }
  };

  const formatHour = (hour: number) => {
    if (hour === 0) return '12:00 AM';
    if (hour < 12) return `${hour}:00 AM`;
    if (hour === 12) return '12:00 PM';
    return `${hour - 12}:00 PM`;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <Button
            id="back-to-monitors"
            variant="ghost"
            size="sm"
            onClick={onBack}
            leftIcon={<ChevronLeft className="w-3.5 h-3.5" />}
            aria-label="Back to all monitors"
            className="mb-2 text-[12px] text-neutral-400 hover:text-neutral-600"
          >
            All monitors
          </Button>
          <h3 className="text-[16px] font-semibold text-neutral-900 truncate">
            {currentMonitor.name}
          </h3>
          {currentMonitor.lead_table_name && currentMonitor.lead_table_id && (
            <Button
              id="go-to-dataset"
              variant="link"
              size="sm"
              onClick={handleGoToDataset}
              rightIcon={<ExternalLink className="w-3 h-3 flex-shrink-0" />}
              aria-label={`Go to dataset ${currentMonitor.lead_table_name}`}
              className="mt-1 text-[12px] text-neutral-500 hover:text-primary-600"
            >
              <span className="truncate">{currentMonitor.lead_table_name}</span>
            </Button>
          )}
        </div>
        <Button
          id="toggle-monitor-status"
          variant={isEnabled ? 'success' : 'secondary'}
          size="sm"
          onClick={handleToggleStatus}
          disabled={isUpdatingMonitor}
          loading={isUpdatingMonitor}
          leftIcon={!isUpdatingMonitor ? (isEnabled ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />) : undefined}
          aria-label={isEnabled ? 'Pause monitor' : 'Enable monitor'}
          className="rounded-full"
        >
          {isEnabled ? 'Pause' : 'Enable'}
        </Button>
      </div>

      {currentMonitor.last_error && (
        <div className="flex items-start gap-2 px-3 py-2.5 bg-error-50 rounded-lg">
          <AlertCircle className="w-4 h-4 text-error-500 flex-shrink-0 mt-0.5" />
          <p className="text-[12px] text-error-600">{currentMonitor.last_error}</p>
        </div>
      )}

      <MonitorInfoCard monitor={currentMonitor} isEnabled={isEnabled} />

      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-[13px] font-semibold text-neutral-900">Settings</h4>
          {!isEditingSchedule && (
            <Button
              id="edit-schedule"
              variant="ghost"
              size="sm"
              onClick={() => setIsEditingSchedule(true)}
              aria-label="Edit monitor schedule"
            >
              Edit
            </Button>
          )}
        </div>

        {isEditingSchedule ? (
          <MonitorScheduleEditor
            schedule={editSchedule}
            maxResults={editMaxResults}
            isUpdating={isUpdatingMonitor}
            onScheduleChange={setEditSchedule}
            onMaxResultsChange={setEditMaxResults}
            onSave={handleSaveSchedule}
            onCancel={handleCancelEdit}
          />
        ) : (
          <div className="flex items-center gap-6 text-[13px]">
            <div>
              <span className="text-neutral-500">Frequency:</span>{' '}
              <span className="font-medium text-neutral-900 capitalize">{editSchedule.frequency}</span>
            </div>
            <div>
              <span className="text-neutral-500">Time:</span>{' '}
              <span className="font-medium text-neutral-900">{formatHour(editSchedule.hourOfDay)}</span>
            </div>
            <div>
              <span className="text-neutral-500">Max:</span>{' '}
              <span className="font-medium text-neutral-900">{currentMonitor.max_results}</span>
            </div>
          </div>
        )}
      </div>

      {!isEditingSchedule && (
        <MonitorRunHistory
          runs={monitorRuns}
          isLoading={isLoadingRuns}
          onRefresh={loadMonitorRuns}
        />
      )}

      {!isEditingSchedule && (
        <div className="pt-4 border-t border-neutral-100">
          <Button
            id="delete-monitor"
            variant="ghost"
            size="sm"
            onClick={() => setShowDeleteConfirm(true)}
            aria-label="Delete this monitor"
            className="text-neutral-400 hover:text-error-600"
          >
            Delete monitor
          </Button>
        </div>
      )}

      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteMonitor}
        title="Delete Monitor"
        message={`Are you sure you want to delete "${currentMonitor.name}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
        isLoading={isDeletingMonitor}
      />
    </div>
  );
};
